import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import LayersIcon from '@mui/icons-material/Layers';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    IconButtonProps,
    Paper,
    Popover,
    Radio,
    RadioGroup,
    Stack,
    StackProps,
    Tooltip,
    Typography,
} from '@mui/material';
import { StandardCSSProperties } from '@mui/system';
import { config as AtlasConfig } from '@sofarocean/atlas';
import { useWeatherInfo } from '@sofarocean/atlas/contexts/WeatherContext';
import { spotterImageGray } from '@sofarocean/atlas/images';
import { WeatherMapColorScale } from '@sofarocean/atlas/maps';
import { extendedPalette } from '@sofarocean/atlas/theme';
import { RasterWeatherLayer, VectorWeatherLayer } from '@sofarocean/atlas/types';
import { MapboxContext } from '@sofarocean/mapbox-context';
import {
    ReactNode,
    forwardRef,
    useContext,
    useEffect,
    useId,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import mapSatelliteImg from '@/assets/map/map-satellite.png';
import mapTerrainImg from '@/assets/map/map-terrain.png';
import { SpotterFamily } from '@/common/types';
import { PopoutButtonHandle } from '@/components/PopoutButton';
import { SpotIcon } from '@/components/data/ColorIcon';
import { Timestamp } from '@/components/data/Timestamp';
import { SPOTTER_ICONS } from '@/components/map/SpotterMap';
import { InfoTip } from '@/components/ui/InfoTip';
import { Switch } from '@/components/ui/Switch';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { SpotterListFilter, SpotterMapLayers, useMapUiContext } from '@/context/MapUiContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGeoLocation } from '@/hooks/useGeoLocation';
import { useNow } from '@/hooks/useNow';
import { mapInfoBlue } from '@/theme';
import { locationForTrackingMetrics } from '@/utils';

const LOCATION_TIMEOUT_MS = 60_000;

export const MyLocationButton = () => {
    const { showMyLocation, setShowMyLocation } = useMapUiContext();
    const { coords, permission, requestLocation } = useGeoLocation();
    const mixpanel = useMixpanel();

    /*
        Timeout: if we're loading (showMyLocation && !coords) for too long, give up
        and let the user try again (in case it's a transient GPS issue rather than a permission issue)
     */
    useEffect(() => {
        if (!showMyLocation || coords) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setShowMyLocation(false);
        }, LOCATION_TIMEOUT_MS);

        return () => clearTimeout(timeoutId);
    }, [showMyLocation, coords, setShowMyLocation]);

    async function onClick() {
        mixpanel?.track('Click toggle My location', { targetState: !showMyLocation });
        if (!coords) {
            // First time: request location (triggers prompt on Safari)
            // Set showMyLocation true immediately to show spinner
            setShowMyLocation(true);
            try {
                await requestLocation();
            } catch (e: unknown) {
                const error = e as GeolocationPositionError;
                // For permission denied (code 1), hide the button/turn off
                if (error.code === 1) {
                    mixpanel?.track('My location permission denied');
                    setShowMyLocation(false);
                }
                // For transient errors (code 2, 3), keep button ON - watch will succeed eventually
            }
        } else {
            // Already have location, just toggle visibility
            setShowMyLocation(!showMyLocation);
        }
    }

    if (permission === 'denied') {
        return null;
    }

    return (
        <Paper
            sx={{
                backgroundColor: 'white',
            }}
        >
            <IconButton onClick={onClick} disabled={showMyLocation && !coords}>
                {!showMyLocation && <LocationSearchingIcon />}
                {showMyLocation && !coords && <CircularProgress size={24} />}
                {showMyLocation && coords && <MyLocationIcon color="info" />}
            </IconButton>
        </Paper>
    );
};

export const MapBaseLayerToggle = ({
    noSatellite,
    size = '2em',
    sx,
}: {
    noSatellite?: boolean;
    size?: StandardCSSProperties['height'];
    sx?: IconButtonProps['sx'];
}) => {
    const { baseLayer, setBaseLayer } = useMapUiContext();
    const mixpanel = useMixpanel();
    const { map } = useContext(MapboxContext);

    function onClick() {
        const targetLayer = baseLayer === 'satellite' ? 'terrain' : 'satellite';
        setBaseLayer(targetLayer);
        mixpanel?.track('Click toggle map base layer', {
            targetLayer,
            zoom: map && Math.round(map.getZoom()),
            center: locationForTrackingMetrics(map?.getCenter()),
        });
    }

    useEffect(() => {
        if (!map) return;

        map.setLayoutProperty(
            'satellite',
            'visibility',
            baseLayer === 'satellite' && !noSatellite ? 'visible' : 'none'
        );
    }, [map, baseLayer, noSatellite]);

    if (noSatellite) {
        // no point in showing at all if you can't switch to satellite (incompatible with weather)
        return false;
    }

    return (
        <Paper
            sx={{
                backgroundColor: 'white',
            }}
        >
            <Tooltip title="Toggle between satellite and terrain" placement="left" enterDelay={300}>
                <IconButton onClick={onClick} sx={{ p: 0.5, ...sx }}>
                    <Box
                        component="img"
                        sx={{ width: size, height: size }}
                        src={baseLayer === 'satellite' ? mapTerrainImg : mapSatelliteImg}
                    />
                </IconButton>
            </Tooltip>
        </Paper>
    );
};

export const SpotterTypeSelector = () => {
    const { spotterLayers } = useMapUiContext();
    const mapSpottersGridFlag = useFeatureFlag('mapSpottersGrid');
    const mapSpottersSharedFlag = useFeatureFlag('mapSpottersShared');

    const anySpotterTypesEnabled = Object.values(spotterLayers).filter((x) => !!x).length > 0;

    if (!(mapSpottersSharedFlag || mapSpottersGridFlag)) {
        // if the only spotters are your own, no need for a legend or layer switches
        return false;
    }

    return (
        <MapPopoverMenu
            button={
                <Paper sx={{ p: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {!anySpotterTypesEnabled && (
                            <Stack direction="row" spacing={0.5}>
                                <Box
                                    component="img"
                                    style={{ height: '24px', position: 'relative', top: '-2px' }}
                                    src={spotterImageGray}
                                    alt="Gray Spotter"
                                />
                                <Typography>Show Spotters</Typography>
                            </Stack>
                        )}
                        {anySpotterTypesEnabled && (
                            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" justifyContent="left">
                                {spotterLayers.mySpotters && <SpotterLegendItem spotterType="mine" />}
                                {spotterLayers.gridSpotters && <SpotterLegendItem spotterType="grid" />}
                                {spotterLayers.sharedSpotters && <SpotterLegendItem spotterType="shared" />}
                            </Stack>
                        )}
                        <ArrowDropDownIcon className="hoverIcon" />
                    </Stack>
                </Paper>
            }
        >
            <Paper
                sx={{
                    backgroundColor: 'white',
                    p: 1,
                }}
            >
                <Stack>
                    <FormLabel id="spotter-group-label">Spotters</FormLabel>
                    <SpotterTypeSwitch spotterType="mine" layerName="mySpotters" />
                    {mapSpottersGridFlag && <SpotterTypeSwitch spotterType="grid" layerName="gridSpotters" />}
                    {mapSpottersSharedFlag && <SpotterTypeSwitch spotterType="shared" layerName="sharedSpotters" />}
                </Stack>
            </Paper>
        </MapPopoverMenu>
    );
};

export const SpotterActiveFilterSelector = ({ spotterFamily }: { spotterFamily: SpotterFamily }) => {
    const mixpanel = useMixpanel();
    const popoverMenuRef = useRef<PopoutButtonHandle>(null);
    const {
        setSpotterListOptions,
        spotterListOptions: { filter },
    } = useMapUiContext();

    return (
        <MapPopoverMenu
            ref={popoverMenuRef}
            button={
                <Paper sx={{ p: 1 }}>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Box
                            component="img"
                            style={{ height: '24px', position: 'relative' }}
                            src={SPOTTER_ICONS[spotterFamily].img}
                        />
                        <Typography>{filter === 'active' ? 'Active' : 'All'}</Typography>
                        <ArrowDropDownIcon className="hoverIcon" sx={{ mx: -0.5 }} />
                    </Stack>
                </Paper>
            }
        >
            <Paper
                sx={{
                    backgroundColor: 'white',
                    p: 1,
                    pl: 2,
                }}
            >
                <Stack>
                    <RadioGroup
                        defaultValue="active"
                        value={filter}
                        onChange={(event) => {
                            const filter = event.target.value as SpotterListFilter;
                            setSpotterListOptions({ filter });
                            popoverMenuRef.current?.close();
                            mixpanel?.track('Spotter list filter', { filter });
                        }}
                    >
                        <FormControlLabel value="all" control={<Radio />} label={`All public Spotters`} />
                        <FormControlLabel value="active" control={<Radio />} label={`Active in the last 24 hours`} />
                    </RadioGroup>
                </Stack>
            </Paper>
        </MapPopoverMenu>
    );
};

const SpotterTypeSwitchIcon = ({ src }: { src: string }) => (
    <Box
        component="img"
        style={{ height: '28px', position: 'relative', top: '-4px', left: '-2px' }}
        src={src}
        alt="Spotter"
    />
);

const SpotterTypeSwitch = ({
    spotterType,
    layerName,
}: {
    spotterType: SpotterFamily;
    layerName: keyof SpotterMapLayers;
}) => {
    const mixpanel = useMixpanel();
    const { spotterLayers, setSpotterLayer } = useMapUiContext();
    const { img, label } = SPOTTER_ICONS[spotterType];

    function handleClick(newValue: boolean) {
        mixpanel?.track('Click toggle Spotter layer', { layerName, targetState: !newValue });
        setSpotterLayer(layerName, newValue);
    }

    return (
        <FormControlLabel
            control={
                <Switch
                    sx={{
                        '.MuiSwitch-track': { m: 'auto', height: 0.8, width: 0.8 },
                    }}
                    color="default"
                    icon={<SpotterTypeSwitchIcon src={spotterImageGray} />}
                    checkedIcon={<SpotterTypeSwitchIcon src={img} />}
                    checked={spotterLayers[layerName]}
                    onChange={(event) => handleClick(event.target.checked)}
                />
            }
            label={label}
        />
    );
};

const SpotterLegendItem = ({ spotterType }: { spotterType: SpotterFamily }) => {
    const { color, img, label } = SPOTTER_ICONS[spotterType];

    return (
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
            <Box
                component="img"
                style={{ height: '24px', position: 'relative', top: '-1px' }}
                src={img}
                alt={`${color} Spotter`}
            />
            <Typography>{label}</Typography>
        </Stack>
    );
};

export const WeatherLayerSelector = () => {
    const mixpanel = useMixpanel();
    const popoverMenuRef = useRef<PopoutButtonHandle>(null);
    const { weatherLayers, setWeatherLayer } = useMapUiContext();
    const weatherMapFlag = useFeatureFlag('weatherMap');

    const directionVectorLabel = useMemo(() => {
        switch (weatherLayers.raster) {
            case 'currents':
                return 'Current Direction';
            case 'wind':
                return 'Wind Direction';
            case 'combinedWaves':
                return 'Wave Direction';
            default:
                return null;
        }
    }, [weatherLayers.raster]);

    function handleChangeRasterLayer(layer: RasterWeatherLayer | undefined) {
        mixpanel?.track('Click change Weather layer', { targetLayer: layer ?? 'none' });
        setWeatherLayer('raster', layer);
        popoverMenuRef.current?.close();
    }

    function handleClickVectorLayer(layer: VectorWeatherLayer, newValue: boolean) {
        mixpanel?.track('Click toggle Weather layer', { layer: layer, targetState: newValue });
        setWeatherLayer('vector', newValue ? layer : undefined);
    }

    if (!weatherMapFlag) {
        return null;
    }

    return (
        <MapPopoverMenu
            ref={popoverMenuRef}
            button={
                <Paper sx={{ p: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <LayersIcon className="hoverIcon" />
                        {!weatherLayers.raster && <Typography>Weather Layers</Typography>}
                        {weatherLayers.raster && (
                            <div style={{ width: '200px' }}>
                                <WeatherMapColorScale variable={weatherLayers.raster} />
                            </div>
                        )}
                        <ArrowDropDownIcon className="hoverIcon" />
                    </Stack>
                </Paper>
            }
        >
            <Paper
                sx={{
                    backgroundColor: 'white',
                    p: 1,
                    pl: 2,
                }}
            >
                <Stack>
                    <FormControl>
                        <FormLabel id="raster-group-label">Base</FormLabel>
                        <RadioGroup
                            aria-labelledby="raster-group-label"
                            defaultValue="combinedWaves"
                            name="raster-group"
                            value={weatherLayers.raster ?? 'none'}
                            onChange={(event) =>
                                handleChangeRasterLayer(
                                    event.target.value === 'none'
                                        ? undefined
                                        : (event.target.value as RasterWeatherLayer)
                                )
                            }
                        >
                            <FormControlLabel value="combinedWaves" control={<Radio />} label="Waves" />
                            <FormControlLabel value="currents" control={<Radio />} label="Currents" />
                            <FormControlLabel value="barometricPressure" control={<Radio />} label="Pressure" />
                            <FormControlLabel value="wind" control={<Radio />} label="Wind" />
                            <FormControlLabel value="windGust" control={<Radio />} label="Gust" />
                            <FormControlLabel value="visibility" control={<Radio />} label="Visibility" />
                            <FormControlLabel value="precipitation" control={<Radio />} label="Precipitation" />
                        </RadioGroup>
                    </FormControl>
                    <FormLabel id="overlay-group-label">Overlays</FormLabel>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={weatherLayers.vector === VectorWeatherLayer.WindBarbs}
                                onChange={(event) =>
                                    handleClickVectorLayer(VectorWeatherLayer.WindBarbs, event.target.checked)
                                }
                            />
                        }
                        label="Wind barbs"
                    />
                    {!!directionVectorLabel && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={weatherLayers.vector === VectorWeatherLayer.Arrows}
                                    onChange={(event) =>
                                        handleClickVectorLayer(VectorWeatherLayer.Arrows, event.target.checked)
                                    }
                                />
                            }
                            label={directionVectorLabel}
                        />
                    )}
                </Stack>
            </Paper>
        </MapPopoverMenu>
    );
};

export const ModelSelector = () => {
    const mixpanel = useMixpanel();
    const popoverMenuRef = useRef<PopoutButtonHandle>(null);
    const { weatherModel, setWeatherModel } = useMapUiContext();
    const weatherMapFlag = useFeatureFlag('weatherMap');
    const { activeInitTimeByModel } = useWeatherInfo();

    function handleChangeWeatherModel(model: string | undefined) {
        mixpanel?.track('Click change Weather model', { targetModel: model ?? 'none' });
        setWeatherModel(model);
        popoverMenuRef.current?.close();
    }

    if (!weatherMapFlag) {
        return null;
    }

    const sofarModelGenDate = activeInitTimeByModel[AtlasConfig.weatherVariables.combinedWaves.modelID];

    return (
        <MapPopoverMenu
            ref={popoverMenuRef}
            button={
                <Paper sx={{ p: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AutoGraphIcon className="hoverIcon" />
                        <Typography>{weatherModel ? 'Sofar Weather' : 'Model'}</Typography>
                        <ArrowDropDownIcon className="hoverIcon" />
                    </Stack>
                </Paper>
            }
        >
            <Paper
                sx={{
                    backgroundColor: 'white',
                    p: 1,
                    pl: 2,
                }}
            >
                <Stack>
                    <FormControl>
                        <FormLabel id="model-group-label">Model</FormLabel>
                        <RadioGroup
                            aria-labelledby="model-group-label"
                            defaultValue="none"
                            name="model-group"
                            value={weatherModel ?? 'none'}
                            onChange={(event) =>
                                handleChangeWeatherModel(event.target.value === 'none' ? undefined : event.target.value)
                            }
                        >
                            <FormControlLabel value="none" control={<Radio />} label="None" />
                            <FormControlLabel
                                value="sofar"
                                sx={{ alignItems: 'start' }}
                                control={<Radio />}
                                label={
                                    <Typography sx={{ mt: '12px' }}>
                                        Sofar Weather
                                        {sofarModelGenDate && (
                                            <Typography component="span" variant="minorLabel" sx={{ display: 'block' }}>
                                                Model init time
                                                <InfoTip iconSize="inherit">
                                                    In weather forecasting, the <i>init time</i> is the starting time
                                                    from which a forecast is generated.
                                                </InfoTip>
                                                <br />
                                                <Timestamp value={sofarModelGenDate} type="relative" />
                                            </Typography>
                                        )}
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </FormControl>
                </Stack>
            </Paper>
        </MapPopoverMenu>
    );
};

const MapPopoverMenu = forwardRef<PopoutButtonHandle, { button: ReactNode; children: ReactNode }>(
    function MapPopoverMenu({ button, children }, ref) {
        useImperativeHandle(
            ref,
            () => ({
                close() {
                    handleClose();
                },
            }),
            []
        );
        const id = useId();
        const [anchorEl, setAnchorEl] = useState<Element | null>(null);

        const handleClick = (event: React.MouseEvent<Element>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const open = Boolean(anchorEl);

        return (
            <>
                <Box
                    sx={{
                        cursor: 'pointer',
                        '& .hoverIcon': {
                            color: extendedPalette.neutralDark,
                        },
                        '&:hover .hoverIcon': {
                            color: extendedPalette.black,
                        },
                    }}
                    aria-describedby={open ? id : undefined}
                    onClick={handleClick}
                >
                    {button}
                </Box>
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {children}
                </Popover>
            </>
        );
    }
);

export const MapTimestamp = () => {
    const now = useNow(500);
    const { selectedTimestamp } = useTimelineNavigationContext();

    return (
        <Paper
            sx={{
                backgroundColor: 'white',
                px: 2,
                height: '32px',
                borderRadius: '16px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Timestamp variant="body2" value={selectedTimestamp ?? now} unit="datetime" type="medium" />
        </Paper>
    );
};

export const ToggleLiveTime = () => {
    const { isTrackingLive, setTrackingLive } = useDateRangeContext();

    return (
        <Paper
            sx={{
                backgroundColor: isTrackingLive ? mapInfoBlue : 'white',
            }}
        >
            <Button
                onClick={() => setTrackingLive(true)}
                sx={{ '&.Mui-disabled': { color: isTrackingLive ? 'white' : undefined } }}
                disabled={isTrackingLive}
            >
                <SpotIcon
                    sx={{ mr: '8px', height: '0.5em', width: '0.5em' }}
                    color={isTrackingLive ? 'success.light' : 'lightgray'}
                />
                Live view
            </Button>
        </Paper>
    );
};

export const MapControlsStack = ({ sx, hideBelowSizePx, ...props }: StackProps & { hideBelowSizePx?: number }) => {
    const { map } = useContext(MapboxContext);
    const [hide, setHide] = useState(true);
    const [rendered, setRendered] = useState(false);

    /**
     * Effect: When the map gets very small, hide controls (makes it look a little better in a narrow slice
     * where the controls aren't going to be useful anyway)
     */
    useEffect(() => {
        if (!map) {
            return;
        }

        const onResize = () => {
            const { width, height } = map.getCanvas();
            setHide(!!hideBelowSizePx && (width < hideBelowSizePx || height < hideBelowSizePx));
        };
        const onRender = () => {
            if (!rendered) {
                onResize();
                setRendered(true);
            }
        };

        map.on('render', onRender);
        map.on('resize', onResize);

        return () => {
            map.off('resize', onResize);
            map.off('render', onRender);
        };
    }, [map, hideBelowSizePx, rendered]);

    if (hide) {
        return false;
    }

    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            justifyContent="flex-end"
            gap={1}
            sx={{
                position: 'absolute',
                zIndex: 100,
                ...sx,
            }}
            {...props}
        />
    );
};
